import { useSyncExternalStore } from "react";

type Wishlist = {
  listName: string;
  listType: string;
  products: string[];
};

type States = {
  localLists: Wishlist[];
  localListsValid: boolean;
};

type Reducers = {
  updateLocalList: (arg: { listName: string; listType: string; products: string[] }) => void;
  deleteLocalList: (listType: string) => void;
  validateLocalLists: () => void;
  resetLocalLists: () => void;
};

const STORAGE_KEY = "wishlist-lists";

const initialState: States = {
  localLists: [],
  localListsValid: false
};

let internalState: States = { ...initialState };
const listeners = new Set<() => void>();

function loadFromStorage() {
    if (typeof window === "undefined") return;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
                internalState = { ...internalState, ...parsed };
            }
        }
    } catch (e) {
        // ignore
    }
}

function saveToStorage() {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ localLists: internalState.localLists })
        );
    } catch (e) {
        // ignore
    }
}

function notify() {
    for (const l of Array.from(listeners)) l();
}

loadFromStorage();

function updateLocalList(arg: { listName: string; listType: string; products: string[] }) {
    const { listName, listType, products } = arg;
    let currentLists: Wishlist[] = internalState.localLists || [];

    const existingList = currentLists.find((list) => list.listType === listType);

    if (existingList) {
        existingList.products = products;
    } else {
        const newList: Wishlist = { listName, listType, products };
        currentLists = [...currentLists, newList];
    }

    internalState = { ...internalState, localLists: [...currentLists] };
    saveToStorage();
    notify();
}

function validateLocalLists() {
    internalState = { ...internalState, localListsValid: true };
    notify();
}

function resetLocalLists() {
    internalState = { localLists: null, localListsValid: false };
    saveToStorage();
    notify();
}

function getState(): States & Reducers {
    return {
        ...internalState,
        updateLocalList,
        validateLocalLists,
        resetLocalLists,
    };
}

export const localWishlistStore = {
    getState,
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    updateLocalList,
    validateLocalLists,
    resetLocalLists,
};

export default function useLocalWishlistStore<T = States & Reducers>(selector?: (s: States & Reducers) => T) {
    const subscribe = (cb: () => void) => {
        listeners.add(cb);
        return () => listeners.delete(cb);
    };

    const getSnapshot = () => (selector ? selector(getState()) : (getState() as unknown as T));

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useLocalWishlistStore as any).persist = {
        clearStorage: () => {
            if (typeof window !== "undefined" && window.localStorage) {
                window.localStorage.removeItem(STORAGE_KEY);
                internalState = { ...internalState, localLists: null };
                notify();
            }
        },
    };
} catch (e) {
    // ignore
}


